/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { TChildren } from '../../typings/common';
import Icon from '../Icon';
import styles from './ButtonBase.module.scss';
import { ButtonAppearance, ButtonSize, IButtonIcon } from './types';

type Combine<First, Second> = Omit<First, keyof Second> & Second;

interface BaseOwnProps {
  children: TChildren;
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  icon?: IButtonIcon;
  squared?: boolean;
  fullWidth?: boolean;
  href?: string;
  isExternal?: boolean;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onClick?: (
    event:
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  isOnlyIconOnMobile?: boolean;
  shouldFitContainer?: boolean;
}

export type BaseButtonProps = Combine<
  React.AllHTMLAttributes<HTMLElement>,
  BaseOwnProps
>;

const ButtonBase: React.FC<BaseButtonProps> = (props) => {
  const {
    children,
    type = 'button',
    appearance = ButtonAppearance.PRIMARY,
    size = ButtonSize.MEDIUM,
    disabled,
    icon,
    squared = false,
    fullWidth = false,
    href,
    isExternal,
    target,
    className,
    onClick,
    isOnlyIconOnMobile = false,
    shouldFitContainer = false,
    ...restProps
  } = props;

  const classes = classNames(
    [
      styles.button,
      styles[appearance],
      styles[size],
      icon?.position && styles[icon.position],
      icon?.glyph && styles[icon.glyph],
      className,
    ],
    {
      [styles.disabled]: disabled,
      [styles.fullWidth]: fullWidth,
      [styles.onlyIconOnMobile]: isOnlyIconOnMobile,
      [styles.fitContainer]: shouldFitContainer,
      [styles.squared]: squared,
    }
  );

  const iconEl = icon && (
    <span className={styles.icon}>
      <Icon glyph={icon.glyph} />
    </span>
  );

  const child = (
    <>
      {icon?.position === 'left' && iconEl}
      <span
        className={classNames(styles.label, {
          'd-none d-block@md': isOnlyIconOnMobile,
        })}>
        {children}
      </span>
      {icon?.position === 'right' && iconEl}
    </>
  );

  if (href) {
    if (disabled) {
      return (
        <button
          className={classes}
          type={type}
          disabled={disabled}
          {...restProps}>
          {child}
        </button>
      );
    }
    return isExternal ? (
      <a
        className={classes}
        href={href}
        target={target}
        onClick={(event) => {
          event.preventDefault();
          if (onClick) {
            onClick(event);
          }
          window.parent.location.href = href;
        }}
        {...restProps}>
        {child}
      </a>
    ) : (
      <Link
        onClick={onClick}
        to={href}
        className={classes}>
        {child}
      </Link>
    );
  }

  return (
    <button
      className={classes}
      type={type}
      disabled={disabled}
      onClick={onClick}
      {...restProps}>
      {child}
    </button>
  );
};

export default ButtonBase;
